import { useMemo } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import { Controls, ControlsButton } from '@/components/Controls';
import FullScreenButton from '@/components/FullScreenButton';
import { PageHeading } from '@/components/Layout/Parts';
import { TableHeader } from '@/components/Table';
import Text from '@/components/Text';

import ArticleBox from './components/ArticleBox';
import TemplateSelector from './components/TemplateSelector';
import VariationBlueTrivia from './components/VariationBlueTrivia';
import VariationBoldComment from './components/VariationBoldComment';
import VariationBoldSubtitles from './components/VariationBoldSubtitles';
import VariationBoldTextImage from './components/VariationBoldTextImage';
import VariationBoldTextText from './components/VariationBoldTextText';
import VariationBpDailyShareOne from './components/VariationBpDailyShareOne';
import VariationBpDailyShareTwo from './components/VariationBpDailyShareTwo';
import VariationChats from './components/VariationChats';
import VariationCommentComment from './components/VariationCommentComment';
import VariationDemilkedShareOne from './components/VariationDemilkedShareOne';
import VariationDemilkedShareTwo from './components/VariationDemilkedShareTwo';
import VariationFive from './components/VariationFive';
import VariationFour from './components/VariationFour';
import VariationFullImageAndBoxTrivia from './components/VariationFullImageAndBoxTrivia';
import VariationFullImageSubtitle from './components/VariationFullImageSubtitle';
import VariationFullPanda from './components/VariationFullPanda';
import VariationImgChats from './components/VariationImgChats';
import VariationImgCommentComment from './components/VariationImgCommentComment';
import VariationImgImgComment from './components/VariationImgImgComment';
import VariationImgImgSubtitle from './components/VariationImgImgSubtitle';
import VariationImgImgText from './components/VariationImgImgText';
import VariationLRFulltext from './components/VariationLRFulltext';
import VariationLRSubtitles from './components/VariationLRSubtitles';
import VariationMainEditorialTemplate from './components/VariationMainEditorialTemplate';
import VariationMeme from './components/VariationMeme';
import VariationMemeShareImg from './components/VariationMemeShareImg';
import VariationMemeTrivia from './components/VariationMemeTrivia';
import VariationMillionaireTrivia from './components/VariationMillionaireTrivia';
import VariationMillionaireTwoTrivia from './components/VariationMillionaireTwoTrivia';
import VariationNeonTrivia from './components/VariationNeonTrivia';
import VariationNewsCircle from './components/VariationNewsCircle';
import VariationNewsShareImage from './components/VariationNewsShareImage';
import VariationOne from './components/VariationOne';
import VariationSeven from './components/VariationSeven';
import VariationSimpleTrivia from './components/VariationSimpleTrivia';
import VariationSix from './components/VariationSix';
import VariationThree from './components/VariationThree';
import VariationTwo from './components/VariationTwo';
import VariationTwoImagesAndBoxTrivia from './components/VariationTwoImagesAndBoxTrivia';
import VariationTwoImagesPlusTextTrivia from './components/VariationTwoImagesPlusTextTrivia';
import VariationTwoImagesSingleText from './components/VariationTwoImagesSingleText';
import VariationTwoImagesTwoTexts from './components/VariationTwoImagesTwoTexts';
import VariationTypeIt from './components/VariationTypeIt';
import VariationWhiteRight from './components/VariationWhiteRight';
import VariationYouOneOuOne from './components/VariationYouOneOuOne';

export type Template = {
  slug: string;
  name: string;
};

enum CoverImageType {
  FB = 'FB Covers',
  OTHER = 'Other Social Covers',
}

enum TemplatesType {
  NEWS = 'News',
  BPDAILY = 'BP Daily',
  DEMILKED = 'Demilked',
  TRIVIA = 'Trivia',
}

const fbCoverVariations = [
  { slug: 'one', name: 'Left / Right Image + Text' },
  { slug: 'two', name: 'Image + Text' },
  { slug: 'three', name: 'Image + Comment' },
  { slug: 'four', name: 'Simple Text' },
  { slug: 'five', name: 'Full Image + Text' },
  { slug: 'six', name: 'Full Image + Full Text' },
  { slug: 'seven', name: 'Full Image + Comment' },
  { slug: 'lr-full-text', name: 'L/R + full text' },
  { slug: 'lr-subtitles', name: ' L/R + subtitles' },
  { slug: 'comment-comment', name: 'Comment + Comment' },
  { slug: 'img-img-text', name: 'Img + Img + Text' },
  { slug: 'img-img-comment', name: 'Img + Img + Comment' },
  { slug: 'full-img-subtitle', name: 'Full Img + Subtitle' },
  { slug: 'img-img-subtitle', name: 'Img + Img + Subtitle' },
  { slug: 'bold-comment', name: 'Bold Text + Comment' },
  { slug: 'bold-text-text', name: 'Bold Text + Text' },
  { slug: 'bold-subtitles', name: 'Bold Subtitles' },
  { slug: 'bold-text-image', name: 'Bold Text + Image Text' },
  { slug: 'img-comment-comment', name: 'Image + Comment + Comment' },
  { slug: 'img-chats', name: 'Image + Chats' },
  { slug: 'chats', name: 'Chats' },
];

const newsVariations = [
  { slug: 'meme-share-img', name: 'Meme Share Image' },
  { slug: 'news-circle', name: 'News Circle' },
  { slug: 'main-editorial-template', name: 'Main Editorial Template' },
  { slug: 'news-share-image', name: 'News Share Image' },
  { slug: 'two-images-two-texts', name: 'Two Images + Two Texts' },
  { slug: 'two-images-single-text', name: 'Two Images + Single Text' },
  { slug: 'meme', name: 'Meme' },
];

const bpDailyVariations = [
  { slug: 'bpdaily-share-one', name: 'BPDaily Share One' },
  { slug: 'bpdaily-share-two', name: 'BPDaily Share Two' },
];

const demilkedVariations = [
  { slug: 'demilked-share-one', name: 'Demilked Share One' },
  { slug: 'demilked-share-two', name: 'Demilked Share Two' },
];

const triviaVariations = [
  {
    slug: 'trivia-white-right',
    name: 'White Right',
  },
  { slug: 'trivia-full-image-and-box', name: 'Full Image & Box' },
  { slug: 'trivia-two-images-and-box', name: '2 Images & Box' },
  { slug: 'trivia-meme', name: 'Meme' },
  { slug: 'blue-trivia', name: 'Blue Trivia' },
  { slug: 'millionaire-trivia', name: 'Millionaire' },
  { slug: 'millionaire-two-trivia', name: 'Millionaire Two' },
  { slug: 'simple-trivia', name: 'Simple Trivia' },
  { slug: 'neon-trivia', name: 'Neon Trivia' },
  { slug: 'full-panda', name: 'Full Panda' },
  { slug: 'type-it', name: 'Type It' },
  { slug: 'two-img-plus-text', name: '2 Img + Text' },
  { slug: 'one-ou-one', name: 'YOU 101' },
];

const SLUG_TO_GROUP: Record<
  string,
  { coverType: CoverImageType; templatesType?: TemplatesType }
> = Object.fromEntries([
  ...fbCoverVariations.map((t) => [t.slug, { coverType: CoverImageType.FB }]),
  ...newsVariations.map((t) => [
    t.slug,
    { coverType: CoverImageType.OTHER, templatesType: TemplatesType.NEWS },
  ]),
  ...bpDailyVariations.map((t) => [
    t.slug,
    { coverType: CoverImageType.OTHER, templatesType: TemplatesType.BPDAILY },
  ]),
  ...demilkedVariations.map((t) => [
    t.slug,
    { coverType: CoverImageType.OTHER, templatesType: TemplatesType.DEMILKED },
  ]),
  ...triviaVariations.map((t) => [
    t.slug,
    { coverType: CoverImageType.OTHER, templatesType: TemplatesType.TRIVIA },
  ]),
]);

const ButtonsHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StyledBackButton = styled(BackButton)`
  margin-bottom: 0;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CoverImagesGenerator = () => {
  const { type } = useParams();

  const { activeType, activeTemplatesType } = useMemo(() => {
    const grp = type ? SLUG_TO_GROUP[type] : undefined;
    return {
      activeType: grp?.coverType ?? CoverImageType.FB,
      activeTemplatesType: grp?.templatesType ?? TemplatesType.NEWS,
    };
  }, [type]);

  const currentTemplates = useMemo<Template[]>(() => {
    if (activeType === CoverImageType.FB) return fbCoverVariations;

    switch (activeTemplatesType) {
      case TemplatesType.BPDAILY:
        return bpDailyVariations;
      case TemplatesType.DEMILKED:
        return demilkedVariations;
      case TemplatesType.TRIVIA:
        return triviaVariations;
      case TemplatesType.NEWS:
      default:
        return newsVariations;
    }
  }, [activeType, activeTemplatesType]);

  const activeTemplate = useMemo(() => {
    return (
      currentTemplates.find((t) => t.slug === type) ??
      currentTemplates[0] ??
      null
    );
  }, [type, currentTemplates]);

  const renderVariation = () => {
    switch (type) {
      case 'one':
        return <VariationOne />;
      case 'two':
        return <VariationTwo />;
      case 'three':
        return <VariationThree />;
      case 'four':
        return <VariationFour />;
      case 'five':
        return <VariationFive />;
      case 'six':
        return <VariationSix />;
      case 'seven':
        return <VariationSeven />;
      case 'lr-full-text':
        return <VariationLRFulltext />;
      case 'lr-subtitles':
        return <VariationLRSubtitles />;
      case 'comment-comment':
        return <VariationCommentComment />;
      case 'img-img-text':
        return <VariationImgImgText />;
      case 'img-img-comment':
        return <VariationImgImgComment />;
      case 'full-img-subtitle':
        return <VariationFullImageSubtitle />;
      case 'img-img-subtitle':
        return <VariationImgImgSubtitle />;
      case 'bold-text-text':
        return <VariationBoldTextText />;
      case 'bold-comment':
        return <VariationBoldComment />;
      case 'bold-subtitles':
        return <VariationBoldSubtitles />;
      case 'bold-text-image':
        return <VariationBoldTextImage />;
      case 'img-comment-comment':
        return <VariationImgCommentComment />;
      case 'img-chats':
        return <VariationImgChats />;
      case 'chats':
        return <VariationChats />;
      case 'meme-share-img':
        return <VariationMemeShareImg />;
      case 'news-circle':
        return <VariationNewsCircle />;
      case 'main-editorial-template':
        return <VariationMainEditorialTemplate />;
      case 'news-share-image':
        return <VariationNewsShareImage />;
      case 'bpdaily-share-one':
        return <VariationBpDailyShareOne />;
      case 'bpdaily-share-two':
        return <VariationBpDailyShareTwo />;
      case 'demilked-share-one':
        return <VariationDemilkedShareOne />;
      case 'demilked-share-two':
        return <VariationDemilkedShareTwo />;
      case 'two-images-two-texts':
        return <VariationTwoImagesTwoTexts />;
      case 'two-images-single-text':
        return <VariationTwoImagesSingleText />;
      case 'meme':
        return <VariationMeme />;
      case 'trivia-white-right':
        return <VariationWhiteRight />;
      case 'trivia-full-image-and-box':
        return <VariationFullImageAndBoxTrivia />;
      case 'trivia-two-images-and-box':
        return <VariationTwoImagesAndBoxTrivia />;
      case 'trivia-meme':
        return <VariationMemeTrivia />;
      case 'blue-trivia':
        return <VariationBlueTrivia />;
      case 'millionaire-trivia':
        return <VariationMillionaireTrivia />;
      case 'millionaire-two-trivia':
        return <VariationMillionaireTwoTrivia />;
      case 'simple-trivia':
        return <VariationSimpleTrivia />;
      case 'neon-trivia':
        return <VariationNeonTrivia />;
      case 'full-panda':
        return <VariationFullPanda />;
      case 'type-it':
        return <VariationTypeIt />;
      case 'two-img-plus-text':
        return <VariationTwoImagesPlusTextTrivia />;
      case 'one-ou-one':
        return <VariationYouOneOuOne />;
      default:
        return <VariationOne />;
    }
  };

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <PageHeading>Cover Images ‘’</PageHeading>
      <ButtonsHolder>
        <StyledBackButton />
      </ButtonsHolder>
      <ArticleBox />
      <TableHeader>
        <LeftControls>
          <Controls>
            <NavLink to="/general/cover-images/generate/one">
              <ControlsButton active={activeType === CoverImageType.FB}>
                {CoverImageType.FB}
              </ControlsButton>
            </NavLink>
            <NavLink to="/general/cover-images/generate/meme-share-img">
              <ControlsButton active={activeType === CoverImageType.OTHER}>
                {CoverImageType.OTHER}
              </ControlsButton>
            </NavLink>
          </Controls>
          {activeType !== CoverImageType.FB && (
            <Controls>
              <NavLink to="/general/cover-images/generate/meme-share-img">
                <ControlsButton
                  gray
                  blackText
                  active={activeTemplatesType === TemplatesType.NEWS}
                >
                  {TemplatesType.NEWS}
                </ControlsButton>
              </NavLink>
              <NavLink to="/general/cover-images/generate/bpdaily-share-one">
                <ControlsButton
                  gray
                  blackText
                  active={activeTemplatesType === TemplatesType.BPDAILY}
                >
                  {TemplatesType.BPDAILY}
                </ControlsButton>
              </NavLink>
              <NavLink to="/general/cover-images/generate/demilked-share-one">
                <ControlsButton
                  gray
                  blackText
                  active={activeTemplatesType === TemplatesType.DEMILKED}
                >
                  {TemplatesType.DEMILKED}
                </ControlsButton>
              </NavLink>
              <NavLink to="/general/cover-images/generate/trivia-white-right">
                <ControlsButton
                  gray
                  blackText
                  active={activeTemplatesType === TemplatesType.TRIVIA}
                >
                  {TemplatesType.TRIVIA}
                </ControlsButton>
              </NavLink>
            </Controls>
          )}
        </LeftControls>
        <RightControls>
          <TemplateSelector
            templates={currentTemplates}
            activeTemplate={activeTemplate!}
          />
          <FullScreenButton />
        </RightControls>
      </TableHeader>
      {renderVariation()}
    </>
  );
};

export default CoverImagesGenerator;
